import { styled } from '@mui/material/styles';
import { UploadManagerIcon } from './UploadManagerIcon';
import { FormattedMessage } from 'react-intl';
import Button from '../Button/Button';
import IconButton from '../IconButton/IconButton';
import { StyledArrowIcon } from './UploadManager';
import { useUploadContext } from '@/contexts/UploadContext';

export const UploadManagerSummary = ({
  onCancelAll,
  collapsed,
  onExpand,
}: {
  onCancelAll: () => void;
  collapsed: boolean;
  onExpand: () => void;
}) => {
  const { list } = useUploadContext();
  const completed = list.filter(
    (upload) => upload.status === 'completed',
  );
  const failed = list.filter((upload) => upload.status === 'failed');
  const pending = list.filter(
    (upload) => upload.status === 'pending',
  );
  const status =
    completed.length === list.length
      ? 'completed'
      : failed.length > 0
        ? 'failed'
        : 'pending';

  // Uploads are saved per batch, all-or-nothing. Counting failures across the whole manager would
  // describe files from unrelated batches, so the summary reports one failed batch: the latest one
  // in the list, since the manager can hold failures from several.
  const lastFailed = failed[failed.length - 1];
  const failedBatchSize = lastFailed?.batchId
    ? list.filter((upload) => upload.batchId === lastFailed.batchId)
        .length
    : failed.length;
  // Giving up on a batch marks every one of its files failed, so a row's own title only names the
  // culprit when it was not part of a batch. `batchFailedFileName` is the file that actually broke
  // it — absent when the batch died on finalize, where no single file is to blame.
  const failedFileName = lastFailed?.batchId
    ? lastFailed.batchFailedFileName
    : lastFailed?.title;

  return (
    <Root status={status}>
      <UploadManagerIcon status={status} />
      <TextWrapper>
        <StatusText
          status={status}
          pendingLength={pending.length}
          totalLength={list.length}
          failedFileName={failedFileName}
          failedBatchSize={failedBatchSize}
        />
      </TextWrapper>
      <StyledButton
        variant="text"
        onClick={onCancelAll}
        visible={!collapsed}
        aria-label="Dismiss All Button"
      >
        <FormattedMessage
          id="dismissAll"
          defaultMessage="Dismiss All"
        />
      </StyledButton>
      <StyledExpandButton
        visible={collapsed}
        onClick={onExpand}
        aria-label="Expand Button"
      >
        <StyledArrowIcon isOpen={collapsed} />
      </StyledExpandButton>
    </Root>
  );
};

const StatusText = ({
  status,
  pendingLength,
  totalLength,
  failedFileName,
  failedBatchSize,
}: {
  status: 'pending' | 'completed' | 'failed';
  pendingLength: number;
  totalLength: number;
  failedFileName?: string;
  failedBatchSize: number;
}) => {
  switch (status) {
    case 'pending':
      return (
        <FormattedMessage
          id="uploadingNoOfItems"
          defaultMessage="Uploading {pendingLength} of {totalLength} items"
          values={{ pendingLength, totalLength }}
        />
      );
    case 'completed':
      return (
        <FormattedMessage
          id="itemsUploadedSuccessfully"
          defaultMessage="{totalLength} items uploaded successfully"
          values={{ totalLength }}
        />
      );
    case 'failed':
      return (
        <FormattedMessage
          id="batchUploadFailedNothingSaved"
          defaultMessage="{failedFileName} failed to upload. None of the {failedBatchSize} files were saved. Please re-upload the batch."
          values={{
            failedFileName: failedFileName ?? 'A file',
            failedBatchSize,
          }}
        />
      );
  }
};

const Root = styled('div')<{
  status: 'pending' | 'completed' | 'failed';
}>(({ theme, status }) => {
  let backgroundColor = theme.palette.white;

  switch (status) {
    case 'pending':
      backgroundColor = theme.palette.blue500;
      break;
    case 'completed':
      backgroundColor = theme.palette.green500;
      break;
    case 'failed':
      backgroundColor = theme.palette.yellow500;
      break;
  }

  return {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    backgroundColor,
    gap: theme.spacing(2),
    borderRadius: theme.radii.xs,
    transition: 'background 0.2s ease-in-out',
    padding: '14px 16px',
    fontSize: 14,
    fontWeight: 500,
    lineHeight: '20px',

    path: {
      stroke: theme.palette.white,
    },
  };
});

const TextWrapper = styled('div')({
  flexGrow: 1,
});

const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'visible',
})<{ visible: boolean }>(({ theme, visible }) => ({
  color: theme.palette.white,
  opacity: visible ? 1 : 0,
  transition: 'all 0.3s ease-in-out',
  transform: visible ? 'translateX(0)' : 'translateX(130px)',
}));

const StyledExpandButton = styled(IconButton)<{ visible: boolean }>(
  ({ theme, visible }) => ({
    position: 'absolute',
    right: visible ? 16 : 60,
    bottom: 14,
    opacity: visible ? 1 : 0,
    cursor: visible ? 'pointer' : 'default',
    pointerEvents: visible ? 'auto' : 'none',

    transition: 'all 0.3s ease-in-out',

    path: {
      stroke: theme.palette.white,
    },
  }),
);
